import React, { useEffect, useRef, useState } from "react";
import { X, ExternalLink, Minimize2, Maximize2, Play } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { spotifyAlbumLink, spotifyAlbumEmbed, spotifyEmbed, spotifyOpenLink } from "../data/mock";

// Lazy-load Spotify IFrame API once
let apiPromise = null;
const loadSpotifyAPI = () => {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.SpotifyIframeApi) return Promise.resolve(window.SpotifyIframeApi);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    window.onSpotifyIframeApiReady = (IFrameAPI) => {
      window.SpotifyIframeApi = IFrameAPI;
      resolve(IFrameAPI);
    };
    if (!document.querySelector('script[data-spotify-iframe-api]')) {
      const s = document.createElement("script");
      s.src = "https://open.spotify.com/embed/iframe-api/v1";
      s.async = true;
      s.setAttribute("data-spotify-iframe-api", "1");
      document.body.appendChild(s);
    }
    // Safety timeout - resolve null if API never loads (network blocked etc.)
    setTimeout(() => resolve(window.SpotifyIframeApi || null), 8000);
  });
  return apiPromise;
};

const MiniPlayer = () => {
  const { current, open, toggle, stop } = usePlayer();
  const [expanded, setExpanded] = useState(true);
  const [useFallback, setUseFallback] = useState(false);
  const hostRef = useRef(null);
  const controllerRef = useRef(null);

  // Setup or update Spotify controller on album change
  useEffect(() => {
    if (!current || !hostRef.current) return;
    let cancelled = false;
    setUseFallback(false);

    const setup = async () => {
      const api = await loadSpotifyAPI();
      if (cancelled || !hostRef.current) return;
      if (!api) {
        setUseFallback(true);
        return;
      }

      // Switch URI if controller exists
      if (controllerRef.current) {
        try {
          controllerRef.current.loadUri(`spotify:${current.spotifyType || "album"}:${current.id}`);
          setTimeout(() => {
            try {
              controllerRef.current.play();
            } catch (e) {}
          }, 400);
          return;
        } catch (e) {
          try { controllerRef.current.destroy(); } catch (_) {}
          controllerRef.current = null;
        }
      }

      // Create fresh controller
      hostRef.current.innerHTML = "";
      const el = document.createElement("div");
      hostRef.current.appendChild(el);
      try {
        api.createController(
          el,
          { uri: `spotify:${current.spotifyType || "album"}:${current.id}` },
          (EmbedController) => {
            if (cancelled) return;
            controllerRef.current = EmbedController;
            EmbedController.addListener("ready", () => {
              try {
                EmbedController.play();
              } catch (e) {}
            });
          }
        );
      } catch (e) {
        setUseFallback(true);
      }
    };

    setup();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  // Cleanup when player closed entirely
  useEffect(() => {
    if (!current && controllerRef.current) {
      try { controllerRef.current.destroy(); } catch (e) {}
      controllerRef.current = null;
      if (hostRef.current) hostRef.current.innerHTML = "";
    }
  }, [current]);

  if (!current) return null;

  const height = expanded ? 380 : 80;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        } md:hidden`}
        onClick={toggle}
      />
      <div
        className={`fixed z-50 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          open
            ? "bottom-4 right-4 left-4 md:left-auto md:right-6 md:bottom-6 md:w-[420px]"
            : "bottom-4 right-4 left-auto md:right-6 md:bottom-6 w-auto"
        }`}
      >
        {open ? (
          <div className="bg-[#0f0f10] text-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={current.cover}
                  alt={current.title}
                  className="w-10 h-10 rounded object-cover"
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {current.title}
                  </p>
                  <p className="text-xs text-white/60 truncate">
                    {current.subtitle}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <a
                  href={spotifyOpenLink(current.id, current.spotifyType || "album")}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open in Spotify"
                  className="p-2 rounded-lg hover:bg-white/10"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setExpanded((v) => !v)}
                  aria-label="Toggle size"
                  className="p-2 rounded-lg hover:bg-white/10"
                >
                  {expanded ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={stop}
                  aria-label="Close player"
                  className="p-2 rounded-lg hover:bg-white/10"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="bg-[#0f0f10]" style={{ height, transition: "height 300ms ease" }}>
              {useFallback ? (
                <iframe
                  key={current.id}
                  title={current.title}
                  src={spotifyEmbed(current.id, current.spotifyType || "album")}
                  width="100%"
                  height={height}
                  style={{ display: "block", border: 0 }}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              ) : (
                <div ref={hostRef} style={{ width: "100%", height: "100%" }} />
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={toggle}
            className="group flex items-center gap-3 bg-black text-white pl-2 pr-5 py-2 rounded-full shadow-xl border border-white/10 hover:scale-[1.03] transition-transform"
          >
            <span className="relative">
              <img
                src={current.cover}
                alt={current.title}
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                <Play className="w-4 h-4" fill="currentColor" />
              </span>
              <span className="absolute -inset-1 rounded-full border border-white/30 animate-ping opacity-75" />
            </span>
            <span className="text-sm font-medium truncate max-w-[160px]">
              {current.title}
            </span>
          </button>
        )}
      </div>
    </>
  );
};

export default MiniPlayer;
